import React from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Search } from 'lucide-react';
import { Role, Permission } from '@/services/rolesPermissionsService';
import { getPermissionDisplayName, getPermissionDescription } from '@/auth/permissions';

interface AssignPermissionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedRole: Role | null;
  permissions: Permission[];
  selectedPermissions: number[];
  onTogglePermission: (permissionId: number) => void;
  onSelectAll: () => void;
  onDeselectAll: () => void;
  onSubmit: () => void;
  isLoading: boolean;
  searchText: string;
  onSearchChange: (text: string) => void;
}

export const AssignPermissionsModal: React.FC<AssignPermissionsModalProps> = ({
  isOpen,
  onClose,
  selectedRole,
  permissions,
  selectedPermissions,
  onTogglePermission,
  onSelectAll,
  onDeselectAll,
  onSubmit,
  isLoading,
  searchText,
  onSearchChange
}) => {
  // Filtrar permisos basado en el texto de búsqueda
  const filteredPermissions = permissions.filter(permission => {
    if (!searchText) return true;
    
    const searchLower = searchText.toLowerCase();
    const displayName = (permission.display_name || getPermissionDisplayName(permission.name)).toLowerCase();
    const description = (permission.description || getPermissionDescription(permission.name) || '').toLowerCase();
    const category = (permission.category || '').toLowerCase();
    const name = permission.name.toLowerCase();
    
    return displayName.includes(searchLower) || 
           description.includes(searchLower) || 
           category.includes(searchLower) ||
           name.includes(searchLower);
  });

  // Agrupar permisos por categoría
  const groupedPermissions = filteredPermissions.reduce((groups, permission) => {
    const category = permission.category || 'general';
    if (!groups[category]) {
      groups[category] = [];
    }
    groups[category].push(permission);
    return groups;
  }, {} as Record<string, Permission[]>);

  // Ordenar categorías alfabéticamente
  const sortedCategories = Object.keys(groupedPermissions).sort();

  return (
    <Dialog open={isOpen} onOpenChange={(open) => {
      if (!open) onClose();
    }}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            Gestionar permisos para: {selectedRole?.name}
          </DialogTitle>
          <DialogDescription>
            Selecciona o deselecciona los permisos que deseas asignar a este rol.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                placeholder="Buscar permisos..."
                value={searchText}
                onChange={(e) => onSearchChange(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="flex gap-2">
              <Button 
                type="button" 
                variant="outline" 
                size="sm"
                onClick={onSelectAll}
              >
                Seleccionar Todos
              </Button>
              <Button 
                type="button" 
                variant="outline" 
                size="sm"
                onClick={onDeselectAll}
              >
                Deseleccionar Todos
              </Button>
            </div>
          </div>
          <div className="max-h-[50dvh] overflow-y-auto space-y-4">
            {sortedCategories.length === 0 ? (
              <div className="text-center text-muted-foreground py-8">
                No se encontraron permisos que coincidan con la búsqueda
              </div>
            ) : (
              sortedCategories.map(category => (
                <div key={category} className="space-y-2">
                  <div className="font-semibold text-sm text-muted-foreground uppercase tracking-wide border-b pb-1">
                    {category === 'general' ? 'General' : category}
                  </div>
                  <div className="space-y-1">
                    {groupedPermissions[category].map((permission) => (
                      <div key={permission.id} className="flex items-center space-x-2 p-2 hover:bg-gray-50 rounded-md">
                        <Checkbox
                          id={`permission-${permission.id}`}
                          checked={selectedPermissions.includes(permission.id)}
                          onCheckedChange={() => onTogglePermission(permission.id)}
                        />
                        <Label 
                          htmlFor={`permission-${permission.id}`}
                          className="flex-1 cursor-pointer"
                        >
                          <div>
                            <div className="font-medium" title={permission.name}>
                              {permission.display_name || getPermissionDisplayName(permission.name)}
                            </div>
                            <div className="text-sm text-muted-foreground">
                              {permission.description || getPermissionDescription(permission.name) || 'Sin descripción'}
                            </div>
                          </div>
                        </Label>
                      </div>
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>
          <div className="flex gap-2 pt-4">
            <Button 
              onClick={onSubmit}
              disabled={isLoading}
            >
              {isLoading ? (
                <div className="flex items-center gap-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Sincronizando...
                </div>
              ) : (
                'Sincronizar Permisos'
              )}
            </Button>
            <Button 
              variant="outline" 
              onClick={onClose}
            >
              Cancelar
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};